import type { UUID } from '@aline/common';
import { proxy } from 'valtio';

const STORAGE_PREFIX = 'aline.bulkExportTaskIds';

/**
 * Active bulk-export status panels, shown in the shared background-task tray.
 * A server-side export is a long-running background job, so its panel has to
 * outlive the dialog that started it, survive navigation, and survive a full
 * page refresh (the export keeps running server-side either way).
 *
 * The tracked ids are mirrored to localStorage under a per-user key, so a refresh
 * re-hydrates the panels instantly with no network round-trip to hang on, while
 * the next account to sign in on a shared browser never sees the previous user's
 * exports. `userId` is null until `hydrateBulkExportTasks` runs.
 */
export const $bulkExportTasks = proxy<{ exportIds: UUID[]; userId: UUID | null }>({
  exportIds: [],
  userId: null,
});

/**
 * Point the store at the signed-in user and load that user's persisted panels.
 * Safe to call on every render; it only does work when the user changes.
 */
export function hydrateBulkExportTasks(userId: UUID): void {
  if ($bulkExportTasks.userId === userId) return;
  $bulkExportTasks.userId = userId;
  $bulkExportTasks.exportIds = readStoredExportIds(userId);
}

/** Open a status panel for an export (no-op if already tracked); persists it. */
export function addBulkExportTask(exportId: UUID): void {
  if ($bulkExportTasks.exportIds.includes(exportId)) return;
  $bulkExportTasks.exportIds.push(exportId);
  persistExportIds();
}

/** Remove an export's panel (user dismiss, cancel, auto-dismiss, or stale id). */
export function removeBulkExportTask(exportId: UUID): void {
  $bulkExportTasks.exportIds = $bulkExportTasks.exportIds.filter(id => id !== exportId);
  persistExportIds();
}

function storageKey(userId: UUID): string {
  return `${STORAGE_PREFIX}.${userId}`;
}

function readStoredExportIds(userId: UUID): UUID[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is UUID => typeof id === 'string');
  } catch {
    return [];
  }
}

function persistExportIds(): void {
  const { userId } = $bulkExportTasks;
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify($bulkExportTasks.exportIds));
  } catch {
    // Best effort: a storage failure (private mode, quota) just means panels
    // won't survive a refresh, which is the pre-existing behavior.
  }
}
